/**
 * Inspected electrical — the outlets, switches, lights and fans the
 * existing-conditions pass FOUND in the walkthrough video, as a read-only
 * layer over the structure.
 *
 * Nothing here writes to a room; the electrical drawing's own devices
 * (`electricalDevices.js`) are the DESIGN. The inspection's APPLY step puts the
 * same devices into an unedited structure as room objects
 * (`@shared-lib/existing-conditions` `inspectedDevices.js`), after which they
 * are also designed and compare as unchanged. These marks are what the
 * inspection saw, read straight off
 * `structures/{id}/existingConditions/{targetId}` (see
 * reference-docs/existing-conditions/overview.md).
 *
 * Where each device is: the `world` point the pass stored, in the structure's
 * aligned frame, so the plan and the 3D scene draw it untransformed. A light or
 * a ceiling fan is stored at its ATTACHMENT point (canopy, backplate) — the
 * point an electrician needs — not at the middle of the fixture.
 *
 * One device, one entry: a thing seen from two targets is kept on one and the
 * other carries `duplicateOf` (`fold_across_targets`), so those are skipped.
 *
 * Pure: no React, no three.js, no Firestore.
 */

import { roomForDevice } from '@shared-lib/existing-conditions';
import { inwardWallNormalAt } from './electricalDevices';
import { lightSymbolOf } from './lightingClassification';

/**
 * The device types this layer draws, and how. `symbol` is the plan glyph:
 * `device` goes through `drawElectricalDeviceSymbol` with the type itself (the
 * inspection's names for these are the electrical catalog's), the rest through
 * `drawLightSymbol`. `wall` says the device is mounted on a wall, so its
 * symbol grows out of the wall into the room.
 */
export const INSPECTED_ELECTRICAL_TYPES = {
  outlet: { kind: 'outlet', symbol: 'device', wall: true },
  'gfci-outlet': { kind: 'outlet', symbol: 'device', wall: true },
  'outlet-240v': { kind: 'outlet', symbol: 'device', wall: true },
  switch: { kind: 'switch', symbol: 'device', wall: true },
  dimmer: { kind: 'switch', symbol: 'device', wall: true },
  '3way': { kind: 'switch', symbol: 'device', wall: true },
  'ceiling-light': { kind: 'light', symbol: 'surface', wall: false },
  // An emergency light can hang on a wall or a ceiling; where it was found is
  // not recorded as a mount, so it is drawn as the ceiling outlet either way.
  'emergency-light': { kind: 'light', symbol: 'surface', wall: false },
  'wall-light': { kind: 'light', symbol: 'sconce', wall: true },
  'ceiling-fan': { kind: 'fan', symbol: 'fan', wall: false },
  'exhaust-fan': { kind: 'fan', symbol: 'device', wall: false },
};

/**
 * How far apart an as-scanned device and a designed device of the same kind
 * may be and still be ONE location the design keeps. Chosen by the user
 * (2026-10-16): it absorbs the inspection's placement error (a few cm close
 * up, ~10 cm from across a room) and a designer nudging a box; a device moved
 * to the next stud bay or further reads as one removed and one new.
 */
export const ELECTRICAL_MATCH_RADIUS_M = 0.3;

/**
 * One colour per CHANGE, shared by the plan and the 3D markers so the two
 * views read the same; the glyph already says what kind of device it is.
 * `unchanged` an original location the design keeps, `removed` an original
 * location the design drops, `new` a designed location the scan did not have.
 */
export const ELECTRICAL_CHANGE_COLORS = {
  unchanged: '#475569',
  removed: '#dc2626',
  new: '#16a34a',
};

const isPoint = (w) =>
  w && [w.x, w.y, w.z].every((v) => typeof v === 'number' && isFinite(v));

/**
 * The electrical devices of a structure's existing-conditions docs.
 *
 * @param {Array} docs - existing-conditions docs (`{ id, ...data }`)
 * @param {Array} rooms - the structure's rooms, to resolve each doc's room
 * @returns {Array<{
 *   id: string, type: string, kind: string, symbol: string,
 *   position: {x:number, y:number, z:number},
 *   inward: {x:number, z:number}|null,
 *   roomId: string|null, roomName: string|null, story: number|null,
 *   targetId: string, mountMissing: boolean,
 * }>}
 *   `inward` is the room-facing normal of the wall a wall device sits on
 *   (null for a ceiling device, or a wall device on no edge of its room).
 */
export function inspectedElectricalDevices(docs, rooms) {
  const out = [];
  for (const doc of docs || []) {
    (doc.devices || []).forEach((dev, i) => {
      const spec = INSPECTED_ELECTRICAL_TYPES[dev?.type];
      if (!spec || dev.duplicateOf || !isPoint(dev.world)) return;
      // the same rule the inspection's apply step files a device by
      // (a satellite room's id changes on every reprocess)
      const room = roomForDevice(doc, dev, rooms || []);
      const position = { x: dev.world.x, y: dev.world.y, z: dev.world.z };
      out.push({
        id: `${doc.id}:${dev.id ?? i}`,
        type: dev.type,
        kind: spec.kind,
        symbol: spec.symbol,
        position,
        inward: spec.wall && room ? inwardWallNormalAt(position, room) : null,
        roomId: room?.id ?? doc.roomId ?? null,
        roomName: room?.name ?? doc.roomName ?? null,
        story: room?.story ?? null,
        targetId: doc.id,
        mountMissing: !!dev.world.mountMissing,
      });
    });
  }
  return out;
}

/**
 * The devices of the rooms being drawn. `rooms` is what the view is showing
 * (already narrowed by the floor switcher and room isolation), so the layer
 * follows both with no notion of its own of "the current room".
 */
export function devicesInRooms(devices, rooms) {
  const ids = new Set((rooms || []).map((r) => r?.id));
  return (devices || []).filter((d) => ids.has(d.roomId));
}

/**
 * How a DESIGNED object is drawn and compared, or null when it is not one of
 * the kinds this layer compares. A light is classified by `lightSymbolOf` —
 * the electrical drawing's own reader, so a recessed can keeps its symbol —
 * and every other kind by the same table the inspection's names use (they are
 * the electrical catalog's). A floor or counter outlet is an outlet with no
 * wall to face off.
 */
const designedSpecOf = (obj) => {
  const symbol = lightSymbolOf(obj);
  if (symbol) return { kind: 'light', symbol, wall: symbol === 'sconce' };
  const type = typeof obj?.type === 'string' ? obj.type.toLowerCase() : '';
  if (type === 'surface-outlet') {
    return { kind: 'outlet', symbol: 'device', wall: false };
  }
  const spec = INSPECTED_ELECTRICAL_TYPES[type];
  // Lights were answered above; a light type `lightSymbolOf` refused is not
  // one the drawing knows how to show.
  return spec && spec.kind !== 'light' ? spec : null;
};

/**
 * The outlets, switches, lights and fans of the CURRENT design — every room's
 * `objects`, whatever put them there — in the same shape as
 * `inspectedElectricalDevices`.
 */
export function designedElectricalDevices(rooms) {
  const out = [];
  for (const room of rooms || []) {
    for (const obj of room?.objects || []) {
      const spec = designedSpecOf(obj);
      if (!spec || !isPoint(obj.center)) continue;
      const position = { x: obj.center.x, y: obj.center.y, z: obj.center.z };
      out.push({
        id: `design:${obj.id}`,
        type: typeof obj.type === 'string' ? obj.type.toLowerCase() : obj.type,
        kind: spec.kind,
        symbol: spec.symbol,
        position,
        inward: spec.wall ? inwardWallNormalAt(position, room) : null,
        roomId: room.id,
        roomName: room.name ?? null,
        story: room.story ?? null,
      });
    }
  }
  return out;
}

const distance3 = (a, b) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

/**
 * As-scanned against designed: each mark with its `status`.
 *
 * A found device and a designed one of the same KIND (outlet, switch, light,
 * fan) within `radius` are one location the design keeps — drawn once, where
 * it was found, `unchanged`. Pairs are taken NEAREST first and each device
 * pairs at most once, so two outlets side by side cannot both claim the one
 * designed outlet between them. A found device left over is `removed`; a
 * designed one left over is `new`, drawn where the design puts it.
 *
 * The kind, not the type, is compared: a plain outlet replaced by a GFCI in
 * the same box is the same location.
 */
export function compareElectrical(
  found,
  designed,
  radius = ELECTRICAL_MATCH_RADIUS_M
) {
  const pairs = [];
  (found || []).forEach((f, i) =>
    (designed || []).forEach((d, j) => {
      if (f.kind !== d.kind) return;
      const dist = distance3(f.position, d.position);
      if (dist <= radius) pairs.push({ i, j, dist });
    })
  );
  pairs.sort((a, b) => a.dist - b.dist);
  const foundTaken = new Set();
  const designedTaken = new Set();
  for (const { i, j } of pairs) {
    if (foundTaken.has(i) || designedTaken.has(j)) continue;
    foundTaken.add(i);
    designedTaken.add(j);
  }
  return [
    ...(found || []).map((f, i) => ({
      ...f,
      status: foundTaken.has(i) ? 'unchanged' : 'removed',
    })),
    ...(designed || [])
      .filter((_, j) => !designedTaken.has(j))
      .map((d) => ({ ...d, status: 'new' })),
  ];
}
